export const evaluationDocumentationMarkdown = `
# Quality Evaluation

## What does this block do? 

The Evaluation block checks how well another block is performing. It compares the output to a correct answer or analyzes similarity by matching words or overall meaning.


## When to use

**Checking AI-generated summaries**: Use this block to see how well a summary captures the key facts from the original content. This is done using the **Faithfulness** evaluation type.

**Comparing to human-provided answers**: If you have correct answers labeled by humans, this block can check how closely the output matches them by measuring **Accuracy** or **Semantic Similarity**.


## How to use this block

- **Compare this generated value**:  Select the block to evaluate here. (Required)
- **To this source value**:  Add reference data for the evaluation. (Required)
- **Using this method**: Select the evaluation metric to run. (Required)
   - **Accuracy**: This evaluation checks if the generated text exactly matches the correct text. If they are identical, the score is perfect (1). If they differ at all, the score is zero (0).
   - **Semantic similarity**: This metric compares how similar the generated text is to the correct text. It does this by representing each piece of text as a set of numbers (like coordinates in a high-dimensional space), then measuring how close those sets of numbers are to each other. The closer the numbers, the more similar the texts.
   - **Faithfulness**: This metric checks how accurately the generated text reflects the original information. It looks at the facts in the generated value and sees if they match to the source. It provides a score and a review as output. This is often used when summarizing information to ensure the summary is reliable.
 

## Output

- **Results**: The evaluation result of the generated value with respect to the source data and chosen evaluation method.


## Examples

- **Accuracy**. This is a binary evaluation, so the output is 1 if the generated value is equal to the source data, and 0 otherwise.
    - Compare this generated value: "hypertension"
    - To this source value: "hypertension"
    - Output from evaluation block: 
      ~~~json
      {
        "accuracy": 1
      }
      ~~~

- **Faithfulness**. This rating measures how well the generated text reflects the original information. It checks the facts in the generated text to see how many match the original. Note that in this case an LLM based review is also given as part of the output in addition to faithfulness score.
    - Compare this generated value: "Patient is 36 years old and has diabetes and hypertension"
    - To this source value: "The patient is a 35 year old male with a history of diabetes and hypertension."
    - Output from evaluation block: 
      ~~~json
      {
        "faithfulness": {
          "review": "There are 3 claims in the output (patient is 36 years old, patient has diabetes, patient has hypertension) and 2 of them are consistent with the input, so the faithfulness score is 2/3 = 0.66. FINAL ANSWER: {\\"score\\": 0.66}",
          "score": 0.66
        }
      }
      ~~~

`;
